import { ArrowDown } from "lucide-react";
import { SlideFrame, SlideHeader, Reveal } from "@/components/presentation/slides/SlideFrame";
import { MediaPlaceholder } from "@/components/primitives/MediaPlaceholder";
import { cn } from "@/lib/cn";

/**
 * 04 · ComparisonSlide
 *
 * Side-by-side or before/after layout. Each side carries a label, a short
 * description, and an optional visual. The `mode` prop chooses the
 * orientation — `before-after` stacks vertically with a connector arrow,
 * `side-by-side` lays out horizontally.
 */
const ComparisonPanel = ({ label, body, visualLabel, tone = "neutral", testId }) => (
  <div
    data-testid={testId}
    className={cn(
      "flex flex-col gap-5 rounded-xl border bg-card p-6 sm:p-8",
      tone === "muted" && "border-border bg-surface/40",
      tone === "neutral" && "border-border",
      tone === "brand" && "border-brand/30 bg-brand/[0.04]"
    )}
  >
    <div className="haevn-eyebrow">{label}</div>
    <MediaPlaceholder kind="image" label={visualLabel} ratio="16/10" />
    <p className="text-sm leading-relaxed text-muted-foreground">{body}</p>
  </div>
);

export const ComparisonSlide = ({
  eyebrow = "Comparison",
  title = "Today, and what's next.",
  mode = "before-after", // "before-after" | "side-by-side"
  left = {
    label: "Traditional",
    body: "How the experience works today.",
    visualLabel: "Status quo",
  },
  right = {
    label: "HAEVN",
    body: "How the experience works in our world.",
    visualLabel: "New posture",
  },
}) => {
  const isStacked = mode === "before-after";
  return (
    <SlideFrame testId="slide-comparison">
      <SlideHeader eyebrow={eyebrow} title={title} />
      <Reveal delay={2}>
        <div
          data-testid="comparison-layout"
          className={cn(
            "grid gap-6",
            isStacked
              ? "grid-cols-1 mx-auto max-w-3xl"
              : "grid-cols-1 lg:grid-cols-2"
          )}
        >
          <ComparisonPanel
            label={left.label}
            body={left.body}
            visualLabel={left.visualLabel}
            tone="muted"
            testId="comparison-left"
          />
          {isStacked && (
            <div
              className="flex justify-center text-muted-foreground"
              aria-hidden="true"
              data-testid="comparison-connector"
            >
              <ArrowDown className="h-5 w-5" />
            </div>
          )}
          <ComparisonPanel
            label={right.label}
            body={right.body}
            visualLabel={right.visualLabel}
            tone="brand"
            testId="comparison-right"
          />
        </div>
      </Reveal>
    </SlideFrame>
  );
};

export default ComparisonSlide;
